/**
 * 精灵图源管理 Hook（v2）
 * 使用 IndexedDB 持久化多张用户自定义精灵图
 * 支持 v1→v2 自动迁移
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  DEFAULT_SPRITE_CONFIG,
  DB_NAME,
  DB_STORE_NAME,
  DB_VERSION,
  CUSTOM_SPRITE_KEY,
  SPRITES_V2_STORE_NAME,
  ACTIVE_SPRITE_KEY,
  THUMBNAIL_SIZE,
  DEFAULT_SPRITE_ID,
} from './types'
import type { SpriteRecord } from './types'

// ===== IndexedDB 操作封装 =====

// 缓存数据库连接，避免重复打开和连接泄漏
let dbCache: IDBDatabase | null = null

function openDBInternal(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = request.result
      const oldVersion = event.oldVersion

      // 创建 v2 store
      if (!db.objectStoreNames.contains(SPRITES_V2_STORE_NAME)) {
        db.createObjectStore(SPRITES_V2_STORE_NAME, { keyPath: 'id' })
      }

      // v1→v2 迁移：从旧 store 读取数据
      if (oldVersion < 2 && db.objectStoreNames.contains(DB_STORE_NAME)) {
        const tx = (event.target as IDBOpenDBRequest).transaction!
        const oldStore = tx.objectStore(DB_STORE_NAME)
        const getReq = oldStore.get(CUSTOM_SPRITE_KEY)

        getReq.onsuccess = () => {
          const oldRecord = getReq.result
          if (oldRecord?.dataUrl) {
            const newStore = tx.objectStore(SPRITES_V2_STORE_NAME)
            const migratedRecord: SpriteRecord = {
              id: crypto.randomUUID(),
              name: '迁移的精灵图',
              dataUrl: oldRecord.dataUrl,
              thumbnail: '', // 初始化时补生成
              timestamp: oldRecord.timestamp || Date.now(),
            }
            newStore.put(migratedRecord)
            localStorage.setItem(ACTIVE_SPRITE_KEY, migratedRecord.id)
          }
          // 迁移读取完成后再删除旧 store，避免中止待处理请求
          try {
            if (db.objectStoreNames.contains(DB_STORE_NAME)) {
              db.deleteObjectStore(DB_STORE_NAME)
            }
          } catch {
            // 删除失败不影响升级，旧 store 留着无害
          }
        }

        getReq.onerror = () => {
          // 迁移读取失败，仍然尝试删除旧 store
          try {
            if (db.objectStoreNames.contains(DB_STORE_NAME)) {
              db.deleteObjectStore(DB_STORE_NAME)
            }
          } catch {
            // 静默
          }
        }
      }
    }

    request.onsuccess = () => {
      dbCache = request.result
      dbCache.onclose = () => { dbCache = null }
      resolve(dbCache)
    }
    request.onerror = () => reject(request.error)
  })
}

function deleteDB(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

/** 打开数据库（带缓存 + 损坏自动重建） */
async function openDB(): Promise<IDBDatabase> {
  if (dbCache) return dbCache
  try {
    const db = await openDBInternal()
    // 验证 store 存在（防止旧版升级损坏：DB 在版本 2 但 store 缺失）
    if (!db.objectStoreNames.contains(SPRITES_V2_STORE_NAME)) {
      console.warn('[SpritePet] sprites-v2 store 缺失，删除 DB 重建')
      db.close()
      dbCache = null
      await deleteDB()
      return await openDBInternal()
    }
    return db
  } catch (e) {
    // 升级失败（损坏），删除后重建
    console.warn('[SpritePet] IndexedDB 升级失败，删除重建', e)
    dbCache = null
    await deleteDB()
    return await openDBInternal()
  }
}

async function getAllSprites(): Promise<SpriteRecord[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SPRITES_V2_STORE_NAME, 'readonly')
    const store = tx.objectStore(SPRITES_V2_STORE_NAME)
    const req = store.getAll()
    req.onsuccess = () => {
      const records = req.result as SpriteRecord[]
      records.sort((a, b) => a.timestamp - b.timestamp)
      resolve(records)
    }
    req.onerror = () => reject(req.error)
  })
}

async function addSpriteRecord(record: SpriteRecord): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SPRITES_V2_STORE_NAME, 'readwrite')
    const store = tx.objectStore(SPRITES_V2_STORE_NAME)
    const req = store.put(record)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

async function deleteSpriteRecord(id: string): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SPRITES_V2_STORE_NAME, 'readwrite')
    const store = tx.objectStore(SPRITES_V2_STORE_NAME)
    const req = store.delete(id)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

/** 生成缩略图：裁剪首帧 (0,0,128,128) → 缩放到 80×80 → JPEG DataURL */
function generateThumbnail(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = THUMBNAIL_SIZE
      canvas.height = THUMBNAIL_SIZE
      const ctx = canvas.getContext('2d')!
      const frameSize = 128
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(img, 0, 0, frameSize, frameSize, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE)
      resolve(canvas.toDataURL('image/jpeg', 0.8))
    }
    img.onerror = () => resolve('')
    img.src = dataUrl
  })
}

// ===== Hook =====

export function useSpriteStore() {
  const [sprites, setSprites] = useState<SpriteRecord[]>([])
  const [activeId, setActiveIdState] = useState<string>(DEFAULT_SPRITE_ID)
  const [isLoading, setIsLoading] = useState(true)

  // 初始加载
  useEffect(() => {
    const init = async () => {
      try {
        const records = await getAllSprites()

        // 补生成缺失的缩略图（v1 迁移记录）
        const needsThumbnail = records.filter((r) => !r.thumbnail)
        for (const record of needsThumbnail) {
          const thumbnail = await generateThumbnail(record.dataUrl)
          if (thumbnail) {
            record.thumbnail = thumbnail
            await addSpriteRecord(record)
          }
        }

        setSprites(records)

        // 恢复激活项
        const savedActiveId = localStorage.getItem(ACTIVE_SPRITE_KEY)
        if (savedActiveId && savedActiveId !== DEFAULT_SPRITE_ID) {
          const exists = records.some((r) => r.id === savedActiveId)
          if (exists) {
            setActiveIdState(savedActiveId)
          } else {
            localStorage.removeItem(ACTIVE_SPRITE_KEY)
          }
        }
      } catch (e) {
        console.error('[SpritePet] IndexedDB 初始化失败，使用默认精灵图', e)
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [])

  // 派生当前精灵图源（向下兼容）
  const spriteSrc = useMemo(() => {
    if (activeId === DEFAULT_SPRITE_ID) {
      return DEFAULT_SPRITE_CONFIG.src
    }
    const record = sprites.find((r) => r.id === activeId)
    return record?.dataUrl ?? DEFAULT_SPRITE_CONFIG.src
  }, [activeId, sprites])

  const isCustom = activeId !== DEFAULT_SPRITE_ID

  // 添加精灵图
  const addSprite = useCallback(async (dataUrl: string, name?: string): Promise<string> => {
    const thumbnail = await generateThumbnail(dataUrl)
    const record: SpriteRecord = {
      id: crypto.randomUUID(),
      name: name || `精灵图 ${new Date().toLocaleDateString()}`,
      dataUrl,
      thumbnail,
      timestamp: Date.now(),
    }
    await addSpriteRecord(record)
    setSprites((prev) => [...prev, record])

    // 自动激活新添加的精灵图
    setActiveIdState(record.id)
    localStorage.setItem(ACTIVE_SPRITE_KEY, record.id)

    return record.id
  }, [])

  // 删除精灵图
  const removeSprite = useCallback(async (id: string) => {
    await deleteSpriteRecord(id)
    setSprites((prev) => prev.filter((r) => r.id !== id))

    // 如果删除的是当前激活项，回退默认
    setActiveIdState((currentId) => {
      if (currentId === id) {
        localStorage.removeItem(ACTIVE_SPRITE_KEY)
        return DEFAULT_SPRITE_ID
      }
      return currentId
    })
  }, [])

  // 重命名精灵图
  const renameSprite = useCallback(async (id: string, newName: string) => {
    const trimmed = newName.trim()
    if (!trimmed) return
    setSprites((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r
        const updated = { ...r, name: trimmed }
        addSpriteRecord(updated) // 持久化到 IndexedDB
        return updated
      })
    )
  }, [])

  // 切换激活精灵图
  const setActive = useCallback((id: string) => {
    setActiveIdState(id)
    if (id === DEFAULT_SPRITE_ID) {
      localStorage.removeItem(ACTIVE_SPRITE_KEY)
    } else {
      localStorage.setItem(ACTIVE_SPRITE_KEY, id)
    }
  }, [])

  // 恢复默认
  const resetToDefault = useCallback(() => {
    setActiveIdState(DEFAULT_SPRITE_ID)
    localStorage.removeItem(ACTIVE_SPRITE_KEY)
  }, [])

  return {
    sprites,
    activeId,
    spriteSrc,
    isCustom,
    isLoading,
    addSprite,
    removeSprite,
    renameSprite,
    setActive,
    resetToDefault,
  }
}
